import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  CloudArrowDownIcon,
  ArrowPathIcon,
  PauseIcon,
  PlayIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  SignalIcon,
} from '@heroicons/react/24/outline';
import DeviceIcon from '../components/DeviceIcon';
import { format } from 'date-fns';
import axios from '@utils/axios';
import DeviceStatusBadge from '../components/DeviceStatusBadge';
import PriorityBadge from '../components/PriorityBadge';
import { PageHeader, StatCard } from '../components/common';

const HistorySync = () => {
  const [syncStatus, setSyncStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Kept apart from `error` so a failed button press reports itself in place instead of
  // replacing the whole page with the load-failure panel.
  const [actionError, setActionError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchSyncStatus();
    
    // Connect to SSE for real-time updates
    const token = localStorage.getItem('token');
    const sseUrl = import.meta.env.VITE_SSE_BASE_URL || 'http://localhost:3012/api/sse';
    const source = new EventSource(`${sseUrl}/events?token=${token}`);
    
    source.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        handleSSEEvent(data);
      } catch (err) {
        console.error('Error parsing SSE data:', err);
      }
    };
    
    source.onerror = (err) => {
      console.error('SSE Connection error:', err);
    };
    
    return () => {
      if (source) {
        source.close();
      }
    };
  }, []);

  const handleSSEEvent = (data) => {
    // Handle sync-related SSE events
    switch (data.type) {
      case 'history_sync_started':
        setSyncStatus(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            activeSyncs: [
              ...prev.activeSyncs.filter(s => s.deviceId !== data.deviceSerial),
              {
                deviceId: data.deviceSerial,
                state: 'syncing',
                duration: 0,
                progress: {
                  chunks: 0,
                  records: 0,
                  percentComplete: 0
                },
                performance: {
                  recordsPerMinute: 0
                }
              }
            ]
          };
        });
        break;

      case 'history_sync_completed':
        setSyncStatus(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            activeSyncs: prev.activeSyncs.filter(s => s.deviceId !== data.deviceSerial),
            queuedDevices: prev.queuedDevices.filter(d => d.deviceId !== data.deviceSerial)
          };
        });
        break;

      case 'history_sync_paused':
        setSyncStatus(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            isPaused: true,
            activeSyncs: []
          };
        });
        break;

      case 'history_sync_resumed':
        setSyncStatus(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            isPaused: false
          };
        });
        // Refresh status after resume
        setTimeout(fetchSyncStatus, 1000);
        break;

      case 'history_sync_progress':
        setSyncStatus(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            activeSyncs: prev.activeSyncs.map(s => 
              s.deviceId === data.deviceSerial 
                ? { ...s, progress: data.progress }
                : s
            )
          };
        });
        break;
        
      case 'system_status':
        // Refresh sync status when system status updates
        fetchSyncStatus();
        break;
    }
  };
  
  const fetchSyncStatus = async () => {
    try {
      const response = await axios.get('/v1/history/sync/status');
      setSyncStatus(response.data.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching sync status:', err);
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  const pauseSyncs = async () => {
    setActionError(null);
    try {
      await axios.post('/v1/history/sync/pause');
      setTimeout(fetchSyncStatus, 500);
    } catch (err) {
      console.error('Error pausing syncs:', err);
      setActionError(err.response?.data?.error || err.message);
    }
  };

  const resumeSyncs = async () => {
    setActionError(null);
    try {
      await axios.post('/v1/history/sync/resume');
      setTimeout(fetchSyncStatus, 500);
    } catch (err) {
      console.error('Error resuming syncs:', err);
      setActionError(err.response?.data?.error || err.message);
    }
  };

  const triggerSync = async (deviceSerial) => {
    setActionError(null);
    try {
      await axios.post(`/v1/history/${deviceSerial}/trigger-sync`);
      // Status will update via SSE
    } catch (err) {
      console.error('Error triggering sync:', err);
      setActionError(`${deviceSerial}: ${err.response?.data?.error || err.message}`);
    }
  };

  const formatDuration = (ms) => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) return `${days}d ${hours % 24}h`;
    if (hours > 0) return `${hours}h ${minutes % 60}m`;
    if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
    return `${seconds}s`;
  };

  const getSyncStateColor = (state) => {
    switch (state) {
      case 'fetching': return 'text-blue-600';
      case 'waiting': return 'text-yellow-600';
      case 'analyzing': return 'text-purple-600';
      case 'requesting_report': return 'text-indigo-600';
      default: return 'text-gray-600';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <ArrowPathIcon className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">Error: {error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {actionError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <ExclamationTriangleIcon className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-red-800 flex-1">{actionError}</p>
          <button
            onClick={() => setActionError(null)}
            className="text-sm font-medium text-red-700 hover:text-red-900"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Header */}
      <PageHeader
        subtitle={
          <span>
            Monitor and control data synchronization across all devices
            {syncStatus?.isPaused && (
              <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                <PauseIcon className="w-3 h-3 mr-1" />
                Paused
              </span>
            )}
          </span>
        }
        actions={
          (
            <div className="flex gap-2">
              <button
                onClick={pauseSyncs}
                disabled={syncStatus?.isPaused}
                className={`px-4 py-2 text-white rounded-md transition-colors flex items-center gap-2 ${
                  syncStatus?.isPaused
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-yellow-600 hover:bg-yellow-700'
                }`}
              >
                <PauseIcon className="w-5 h-5" />
                Pause All
              </button>
              <button
                onClick={resumeSyncs}
                disabled={!syncStatus?.isPaused}
                className={`px-4 py-2 text-white rounded-md transition-colors flex items-center gap-2 ${
                  !syncStatus?.isPaused
                    ? 'bg-gray-400 cursor-not-allowed'
                    : 'bg-green-600 hover:bg-green-700'
                }`}
              >
                <PlayIcon className="w-5 h-5" />
                Resume All
              </button>
            </div>
          )
        }
      />

      {/* System Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard
          label="Active Syncs"
          value={syncStatus?.activeSyncs?.length || 0}
          valueColor="text-blue-600"
          subtitle={`of ${syncStatus?.config?.maxConcurrent || 3} max`}
          icon={ArrowPathIcon}
          iconColor="text-blue-500"
          delay={0.1}
        />
        <StatCard
          label="Devices with Data"
          value={syncStatus?.queueLength || 0}
          valueColor="text-yellow-600"
          subtitle="pending sync"
          icon={DeviceIcon}
          iconColor="text-yellow-500"
          delay={0.15}
        />
        <StatCard
          label="System Load"
          value={syncStatus?.metrics?.currentLoad || '0%'}
          valueColor="text-green-600"
          subtitle={syncStatus?.config?.adaptiveThrottling ? 'adaptive' : 'fixed'}
          icon={CheckCircleIcon}
          iconColor="text-green-500"
          delay={0.2}
        />
        <StatCard
          label="Last Hour"
          value={syncStatus?.metrics?.lastHourRecords?.toLocaleString() || 0}
          valueColor="text-purple-600"
          subtitle="records synced"
          icon={ClockIcon}
          iconColor="text-purple-500"
          delay={0.25}
        />
      </div>

      {/* Active Syncs */}
      {syncStatus?.activeSyncs?.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <ArrowPathIcon className="w-5 h-5 animate-spin" />
            Active Syncs
          </h3>
          
          <div className="space-y-4">
            {syncStatus.activeSyncs.map((sync, index) => (
              <motion.div
                key={sync.deviceId}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="border border-gray-200 rounded-lg p-4"
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <SignalIcon className="w-5 h-5 text-green-600" />
                    <span 
                      className="font-medium text-blue-600 hover:text-blue-800 cursor-pointer"
                      onClick={() => navigate(`/dashboard/devices/${sync.deviceId}`)}
                    >
                      {sync.deviceId}
                    </span>
                    <span className={`text-sm ${getSyncStateColor(sync.state)}`}>
                      {sync.state.replace('_', ' ')}
                    </span>
                  </div>
                  <span className="text-sm text-gray-600">
                    {formatDuration(sync.duration)}
                  </span>
                </div>
                
                {sync.progress && (
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">
                        {sync.progress.chunks} chunks • {sync.progress.records} records
                      </span>
                      <span className="font-medium">
                        {sync.progress.percentComplete}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${sync.progress.percentComplete}%` }}
                      />
                    </div>
                    {sync.performance?.recordsPerMinute && (
                      <p className="text-xs text-gray-500">
                        ~{Math.round(sync.performance.recordsPerMinute)} records/min
                      </p>
                    )}
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {/* Queued Devices */}
      {syncStatus?.queuedDevices?.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex justify-between items-start mb-4">
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <ClockIcon className="w-5 h-5" />
              Devices with Pending Data
            </h3>

            {/* Priority Legend */}
            <details className="relative">
              <summary className="cursor-pointer text-sm text-blue-600 hover:text-blue-800">
                Priority Guide
              </summary>
              <div className="absolute right-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg p-4 z-10 w-80">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <PriorityBadge priority="critical" />
                    <span className="text-xs text-gray-600">Just connected (&lt;5 min)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <PriorityBadge priority="high" />
                    <span className="text-xs text-gray-600">Online or offline &lt;1 hour</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <PriorityBadge priority="medium" />
                    <span className="text-xs text-gray-600">Offline 1-24 hours</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <PriorityBadge priority="low" />
                    <span className="text-xs text-gray-600">Offline 1-30 days</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <PriorityBadge priority="archive" />
                    <span className="text-xs text-gray-600">Offline &gt;30 days</span>
                  </div>
                </div>
              </div>
            </details>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    Device ID
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    Status
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    Priority
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    Last Seen
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                    Pending Records
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {syncStatus.queuedDevices.map((device) => (
                    <tr key={device.deviceId}>
                      <td className="px-4 py-2 text-sm font-medium">
                        <span
                          className="text-blue-600 hover:text-blue-800 cursor-pointer"
                          onClick={() => navigate(`/dashboard/devices/${device.deviceId}`)}
                        >
                          {device.deviceId}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-sm">
                        <DeviceStatusBadge status={device.status} isOnline={device.isOnline} />
                      </td>
                      <td className="px-4 py-2 text-sm">
                        <PriorityBadge priority={device.priority} showDescription={false} />
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-600">
                        {device.lastSeen ? (
                          <div>
                            <div>{format(new Date(device.lastSeen), 'MMM dd HH:mm')}</div>
                            <div className="text-xs text-gray-500">
                              {formatDuration(device.timeSinceLastSeen || 0)} ago
                            </div>
                          </div>
                        ) : (
                          'Unknown'
                        )}
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-600">
                        {(device.pendingRecords || 0).toLocaleString()}
                      </td>
                    </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Configuration */}
      {syncStatus?.config && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold mb-4">Current Configuration</h3>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-gray-600">Max Concurrent</p>
              <p className="font-medium">{syncStatus.config.maxConcurrent}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Chunk Size</p>
              <p className="font-medium">{syncStatus.config.currentChunkSize} records</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Chunk Delay</p>
              <p className="font-medium">{syncStatus.config.chunkDelay}ms</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Adaptive Throttling</p>
              <p className="font-medium">
                {syncStatus.config.adaptiveThrottling ? (
                  <CheckCircleIcon className="w-5 h-5 text-green-600" />
                ) : (
                  <ExclamationTriangleIcon className="w-5 h-5 text-gray-400" />
                )}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* All Devices Overview */}
      {syncStatus?.devices && syncStatus.devices.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <DeviceIcon className="w-5 h-5" />
            All Devices Sync Status
          </h3>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Device</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Progress</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Pending</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Last Sync</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Rate</th>
                  <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {syncStatus.devices.map((device) => (
                  <tr key={device.deviceSerial} className="hover:bg-gray-50">
                    <td className="px-4 py-2">
                      <div className="flex items-center gap-2">
                        <SignalIcon className={`w-4 h-4 ${device.isOnline ? 'text-green-600' : 'text-gray-400'}`} />
                        <span 
                          className="text-sm font-medium text-blue-600 hover:text-blue-800 cursor-pointer"
                          onClick={() => navigate(`/dashboard/devices/${device.deviceSerial}`)}
                        >
                          {device.deviceSerial}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-2">
                      <span className={`inline-flex items-center gap-1 text-sm ${
                        device.syncState?.currentlySyncing ? 'text-blue-600' :
                        device.isOnline ? 'text-green-600' : 'text-gray-600'
                      }`}>
                        {device.syncState?.currentlySyncing ? (
                          <>
                            <ArrowPathIcon className="w-4 h-4 animate-spin" />
                            Syncing
                          </>
                        ) : device.isOnline ? (
                          <>
                            <CheckCircleIcon className="w-4 h-4" />
                            Ready
                          </>
                        ) : (
                          <>
                            <ExclamationTriangleIcon className="w-4 h-4" />
                            Offline
                          </>
                        )}
                      </span>
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex items-center gap-2">
                        <div className="w-24 bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${device.dataStatus?.syncProgress || 0}%` }}
                          />
                        </div>
                        <span className="text-xs text-gray-600">{device.dataStatus?.syncProgress || 0}%</span>
                      </div>
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-600">
                      {(device.dataStatus?.pendingRecords || 0).toLocaleString()}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-600">
                      {device.syncState?.lastSync ? 
                        format(new Date(device.syncState.lastSync), 'MMM dd HH:mm') : 
                        'Never'
                      }
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-600">
                      {device.performance?.avgRecordsPerHour || 0}/hr
                    </td>
                    <td className="px-4 py-2 text-center">
                      {device.isOnline && !device.syncState?.currentlySyncing && device.dataStatus?.pendingRecords > 0 && (
                        <button
                          onClick={() => triggerSync(device.deviceSerial)}
                          className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                        >
                          Sync Now
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default HistorySync;